import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Search, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RosterFilterMenu } from 'components/IncidentConfig/roster/RosterFilterMenu';
import {
  persistSidebarCollapsed,
  readSidebarCollapsed,
  RosterChartChromeButtons,
} from 'components/IncidentConfig/roster/RosterChartChromeButtons';
import {
  assignedPositionIds,
  buildRosterFilterChips,
  containingGroupIds,
  seatAndDescendantIds,
  dismissRosterFilterChip,
  EMPTY_ROSTER_FILTERS,
  filterRosterPositions,
  type RosterFilters,
} from 'components/IncidentConfig/roster/rosterPositionFilter';
import { selectUserGuid } from 'slices/commonSelectors';
import { rosterSeatLoad } from 'components/IncidentConfig/hwcgTour/hwcgTourBridge';
import { registerFocIncidentRosterTourApi } from 'components/MapPage/focIncidentRosterTour/focIncidentRosterTourBridge';
import {
  focIncidentRosterHref,
  focIncidentRosterSettingsHref,
} from 'components/MapPage/focIncidentRosterTour/focIncidentRosterTourSteps';
import { registerFocPlanningTourApi } from 'components/MapPage/focPlanningTour/focPlanningTourBridge';
import { useHwcgFlag } from 'utils/featureFlags';
import { indexUsersByGuid } from 'components/IncidentConfig/roster/activationLog/hwcgActivationLogModel';
import {
  currentPeriodLabel,
  partitionRosterByPeriod,
  personSeatActivation,
  plannedPeriodLabel,
  rosterPeriodChoices,
  type HwcgPeriodView,
  type HwcgPersonSeatStatus,
} from 'components/IncidentConfig/roster/rosterPeriods';
import {
  usePlannedOperationalPeriods,
  usePlannedPeriodRosters,
} from 'hooks/incident/usePlannedOperationalPeriods';
import { useHwcgActivationLog } from 'hooks/useHwcgActivationLog';
import {
  useGetIncidentLogs,
  type IncidentLogEntry,
} from 'hooks/incident/useWorkLog';
import { useNavigate, useParams } from 'react-router';
import { useGetPositionsForIncident } from 'hooks/incident/useRoster';
import { StylishConfirmDialog } from 'components/DesignSystems/New/StylishConfirmDialog';
import { useOptionalIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import {
  useActualIncidentId,
  useGroupGuid,
} from 'components/IAP/hooks/commonHooks';
import { HwcgRosterActivationPanel } from 'components/IncidentConfig/roster/HwcgRosterActivationPanel';
import { HwcgRosterActivationProvider } from 'components/IncidentConfig/roster/hwcgRosterActivationContext';
import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import IncidentEditMemberDialog from 'components/IncidentConfig/IncidentEditMemberDialog';
import IncidentNonActiveConfirmDialog from 'components/IncidentConfig/IncidentNonActiveConfirmDialog';
import OrganizationChart, {
  type OrganizationChartHandle,
} from 'components/IncidentConfig/OrganizationChart';
import RosterActions from 'components/IncidentConfig/roster/RosterActions';
import { RosterAnalyticsView } from 'components/IncidentConfig/roster/RosterAnalyticsView';
import { RosterListView } from 'components/IncidentConfig/roster/RosterListView';
import RosterTable from 'components/IncidentConfig/roster/RosterTable';
import RosterTemplateSidebar from 'components/IncidentConfig/roster/RosterTemplateSidebar';
import { RosterViewMode } from 'components/IncidentConfig/roster/types';
type DeleteType = 'Leave Incident' | 'Remove Member' | 'Remove Scheduled';

type DeleteTypeConfig = {
  title: string;
  content: string;
  buttonText: string;
};

const DELETE_TYPE_CONFIG: Record<DeleteType, DeleteTypeConfig> = {
  'Leave Incident': {
    title: 'Leave Incident Confirmation',
    content: 'Do you want to remove yourself from this incident?',
    buttonText: 'Leave Incident',
  },
  'Remove Member': {
    title: 'Remove Member Confirmation',
    content: 'Do you want to delete this incident Member',
    buttonText: 'Remove Member',
  },
  'Remove Scheduled': {
    title: 'Delete Schedule Confirmation',
    content: 'Are you sure you want to delete this scheduled?',
    buttonText: 'Delete Schedule',
  },
};

function openSignInUserIds(
  logs: IncidentLogEntry[] | undefined
): ReadonlySet<string> {
  const ids = new Set<string>();
  (logs ?? []).forEach((entry) => {
    const sessions = (entry.logs ?? []).flatMap((log) => log.sessions ?? []);
    const latest = sessions
      .filter((session) => session.signInTime)
      .sort((a, b) => (a.signInTime < b.signInTime ? 1 : -1))[0];
    if (latest && !latest.signOutTime) ids.add(entry.userGuid);
  });
  return ids;
}

type IncidentRosterViewProps = {
  rosterData: RosterEntry[];
  draftRosterData?: RosterEntry[] | null;
  isRosterLoading: boolean;
  currentUserHasApprovalAuthority?: boolean;
  isTogglingAuthority?: boolean;
  onRemoveMember: (member: RosterEntry) => void;
  onRemoveSelf: (member: RosterEntry) => void;
  onMarkActive: (member: RosterEntry) => void;
  onDeleteScheduleConfirmed: (params: {
    scheduleId: string;
    userGuid: string;
    incidentMemberId: string;
  }) => void;
  onToggleApprovalAuthority?: (member: RosterEntry) => void;
  showApprovalAuthority?: boolean;
  /**
   * @deprecated No longer used — kept only for backward compat with
   *   IapDraftRoster.tsx
   */
  isDraft?: boolean;
};

const IncidentRosterView = ({
  rosterData,
  draftRosterData = null,
  isRosterLoading,
  currentUserHasApprovalAuthority,
  isTogglingAuthority,
  onRemoveMember,
  onRemoveSelf,
  onMarkActive,
  onDeleteScheduleConfirmed,
  onToggleApprovalAuthority,
  showApprovalAuthority = true,
  isDraft = false,
}: IncidentRosterViewProps) => {
  const [memberSelectedForEditing, setMemberSelectedForEditing] =
    useState<RosterEntry>();
  const [isEditMemberDialogOpen, setIsEditMemberDialogOpen] = useState(false);
  const [notActiveMemberDialogOpen, setNotActiveMemberDialogOpen] =
    useState(false);
  const [deactivateRow, setDeactivateRow] = useState<RosterEntry>();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [deleteType, setDeleteType] = useState<DeleteType | null>(null);
  const [deleteMemberData, setDeleteMemberData] = useState<RosterEntry | null>(
    null
  );
  const hwcg = useHwcgFlag();
  const userGuid = useSelector(selectUserGuid);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const { incidentId: routeIncidentId } = useParams<{ incidentId: string }>();
  const actualIncidentId = useActualIncidentId();
  const incidentId = routeIncidentId || actualIncidentId;
  const groupId = useGroupGuid();
  const appliedSeatFilter = useRef(false);
  const [editablePositionIds, setEditablePositionIds] = useState<
    string[] | null
  >(null);
  const positionsQuery = useGetPositionsForIncident();
  const positions = positionsQuery.data ?? [];
  const rosterDataRef = useRef(rosterData);
  rosterDataRef.current = rosterData;
  rosterSeatLoad.viewCount = positions.length;
  rosterSeatLoad.viewFetched = positionsQuery.isFetched;
  const icsFormPdf = useOptionalIcsFormPdfPreview();
  const chartRef = useRef<OrganizationChartHandle>(null);
  const [sidebarCollapsed, setSidebarCollapsed] =
    useState(readSidebarCollapsed);
  const [chartFilters, setChartFilters] =
    useState<RosterFilters>(EMPTY_ROSTER_FILTERS);
  const [tableFilters, setTableFilters] =
    useState<RosterFilters>(EMPTY_ROSTER_FILTERS);
  const periodScheduling = hwcg && !isDraft;
  const planned = usePlannedOperationalPeriods(incidentId, periodScheduling);
  const plannedRosters = usePlannedPeriodRosters(
    incidentId,
    planned.view.periods,
    periodScheduling
  );
  const plannedWithMembers = useMemo(
    () =>
      planned.view.periods.map((period) => ({
        periodNumber: period.periodNumber,
        members: plannedRosters.membersByPeriodId[period.id] ?? [],
      })),
    [planned.view.periods, plannedRosters.membersByPeriodId]
  );
  const activationLog = useHwcgActivationLog(incidentId, {
    enabled: periodScheduling,
  });
  const incidentLogs = useGetIncidentLogs();
  const periodChoices = periodScheduling
    ? rosterPeriodChoices(
        planned.view.currentPeriodNumber,
        planned.view.periods
      )
    : [];
  const partitionFor = useCallback(
    (view: HwcgPeriodView | undefined) =>
      periodScheduling && planned.view.periods.length > 0
        ? partitionRosterByPeriod({
            current: rosterData ?? [],
            planned: plannedWithMembers,
            view: view ?? 'current',
          })
        : null,
    [
      periodScheduling,
      planned.view.periods.length,
      plannedWithMembers,
      rosterData,
    ]
  );
  const chartPeriod = useMemo(
    () => partitionFor(chartFilters.hwcgPeriod),
    [chartFilters.hwcgPeriod, partitionFor]
  );
  const tablePeriod = useMemo(
    () => partitionFor(tableFilters.hwcgPeriod),
    [partitionFor, tableFilters.hwcgPeriod]
  );
  const hwcgPersonStatus = useMemo(() => {
    if (!periodScheduling) return null;
    const byUser = indexUsersByGuid(activationLog.data?.users);
    const signedIn = openSignInUserIds(incidentLogs.data);
    const status: Record<string, HwcgPersonSeatStatus> = {};
    const record = (member: RosterEntry, onCurrentRoster: boolean) => {
      if (status[member.userId]) return;
      const user = byUser[member.userId];
      // Notices sent before seat-level tracking only live in the activation log.
      const legacySent =
        onCurrentRoster &&
        Boolean(
          user && (user.notices.length > 0 || user.lastNoticeAt || user.status)
        );
      const legacy: HwcgPersonSeatStatus['activation'] = !legacySent
        ? null
        : user.status === 'activated'
          ? 'accepted'
          : user.status === 'declined'
            ? 'declined'
            : 'waiting';
      status[member.userId] = {
        activation: personSeatActivation(member) ?? legacy,
        checkedIn: member.active === 1 && Boolean(member.activeTime),
        signedIn: signedIn.has(member.userId),
      };
    };
    const view = chartFilters.hwcgPeriod ?? 'current';
    if (typeof view === 'number' && chartPeriod) {
      chartPeriod.shown.forEach((member) => record(member, false));
    } else {
      (rosterData ?? []).forEach((member) => record(member, true));
      (chartPeriod?.later ?? []).forEach((member) => record(member, false));
    }
    return status;
  }, [
    activationLog.data?.users,
    chartFilters.hwcgPeriod,
    chartPeriod,
    incidentLogs.data,
    periodScheduling,
    rosterData,
  ]);
  // Structural filters only — member free-text search is applied in RosterTable.
  const tableVisiblePositionIds = useMemo(() => {
    if (
      tableFilters.positionIds.length === 0 &&
      tableFilters.categories.length === 0
    ) {
      return null;
    }
    return new Set(
      filterRosterPositions(positions, {
        ...tableFilters,
        search: '',
      }).map((position) => position.id)
    );
  }, [positions, tableFilters]);
  const tableFilterChips = useMemo(
    () => buildRosterFilterChips(tableFilters, positions),
    [positions, tableFilters]
  );
  const [deleteScheduleId, setDeleteScheduleId] = useState<string>();
  const [requestedViewMode, setViewMode] = useState<RosterViewMode>('orgchart');
  const viewMode =
    requestedViewMode === 'list' && !hwcg ? 'orgchart' : requestedViewMode;
  // The bulk send follows the period the visible view is filtered to; "All" sends for the current period.
  const panelPeriodView =
    (viewMode === 'table'
      ? tableFilters.hwcgPeriod
      : chartFilters.hwcgPeriod) ?? 'current';
  const panelPlanned =
    typeof panelPeriodView === 'number'
      ? planned.view.periods.find(
          (period) => period.periodNumber === panelPeriodView
        )
      : undefined;
  const activationPanelPeriod = panelPlanned
    ? {
        plannedPeriodId: panelPlanned.id,
        periodLabel: plannedPeriodLabel(panelPlanned.periodNumber),
        members: plannedRosters.membersByPeriodId[panelPlanned.id] ?? [],
      }
    : {
        plannedPeriodId: null,
        periodLabel: currentPeriodLabel(planned.view.currentPeriodNumber),
        members: rosterData ?? [],
      };
  const showChartChrome = viewMode === 'orgchart' || viewMode === 'analytics';

  useEffect(() => {
    persistSidebarCollapsed(sidebarCollapsed);
  }, [sidebarCollapsed]);

  useEffect(() => {
    return registerFocPlanningTourApi({
      ensureRosterSidebar: () => {
        setViewMode('orgchart');
        setSidebarCollapsed(false);
      },
    });
  }, []);

  useEffect(() => {
    const firstMember = () => rosterDataRef.current?.[0];
    return registerFocIncidentRosterTourApi({
      getLocation: () => ({
        pathname: window.location.pathname,
        search: window.location.search,
      }),
      showTableView: () => setViewMode('table'),
      showOrgChartView: () => setViewMode('orgchart'),
      ensureRosterSidebar: () => {
        setViewMode('orgchart');
        setSidebarCollapsed(false);
      },
      expandFirstMember: () => {
        setViewMode('table');
        window.setTimeout(() => {
          const btn = document.querySelector(
            '[data-tour="roster-member-expand"]'
          );
          if (
            btn instanceof HTMLElement &&
            btn.getAttribute('aria-expanded') !== 'true'
          ) {
            btn.click();
          }
        }, 50);
      },
      collapseFirstMember: () => {
        const btn = document.querySelector(
          '[data-tour="roster-member-expand"]'
        );
        if (
          btn instanceof HTMLElement &&
          btn.getAttribute('aria-expanded') === 'true'
        ) {
          btn.click();
        }
      },
      openEditFirstMember: () => {
        const member = firstMember();
        if (!member) return;
        setMemberSelectedForEditing(member);
        setIsEditMemberDialogOpen(true);
      },
      closeEditMember: () => {
        setIsEditMemberDialogOpen(false);
      },
      focusRosterSearch: () => {
        setViewMode('table');
        window.setTimeout(() => {
          const input = document.querySelector('[data-tour="roster-search"]');
          if (input instanceof HTMLElement) input.focus();
        }, 50);
      },
      openFilters: () => {
        setViewMode('table');
        window.setTimeout(() => {
          const host = document.querySelector('[data-tour="roster-filters"]');
          const button = host?.querySelector('button');
          if (button instanceof HTMLElement) button.click();
        }, 50);
      },
      closeFilters: () => {
        // Popover closes on outside click; Escape is enough for tour cleanup.
        document.dispatchEvent(
          new KeyboardEvent('keydown', { key: 'Escape', bubbles: true })
        );
      },
      navigateToSettings: () => {
        if (!incidentId) return;
        navigateRef.current(focIncidentRosterSettingsHref(incidentId));
      },
      navigateToRosterSurface: () => {
        if (!incidentId) return;
        navigateRef.current(focIncidentRosterHref(incidentId));
      },
    });
  }, [incidentId]);

  useEffect(() => {
    if (!hwcg || appliedSeatFilter.current || isRosterLoading) return;
    if (positions.length === 0) return;
    appliedSeatFilter.current = true;
    const seats = assignedPositionIds(rosterData, userGuid);
    if (seats.length === 0) return;
    const positionIds = containingGroupIds(positions, seats);
    setEditablePositionIds(seatAndDescendantIds(positions, seats));
    setChartFilters((current) => ({ ...current, positionIds }));
    setTableFilters((current) => ({ ...current, positionIds }));
  }, [hwcg, isRosterLoading, positions, rosterData, userGuid]);

  const editMemberButtonClicked = (member: RosterEntry) => {
    setMemberSelectedForEditing(member);
    setIsEditMemberDialogOpen(true);
  };

  const removeMemberButtonClicked = (member: RosterEntry) => {
    setDeleteType('Remove Member');
    setDeleteMemberData(member);
    setShowConfirmDialog(true);
  };

  const removeSelfFromIncidentButtonClicked = (member: RosterEntry) => {
    setDeleteType('Leave Incident');
    setDeleteMemberData(member);
    setShowConfirmDialog(true);
  };

  const onDeleteSchedule = (scheduleId: string, row: RosterEntry) => {
    setDeleteType('Remove Scheduled');
    setDeleteScheduleId(scheduleId);
    setDeleteMemberData(row);
    setShowConfirmDialog(true);
  };

  const onDeleteConfirmation = () => {
    if (!deleteMemberData) {
      return;
    }

    if (deleteType === 'Leave Incident') {
      onRemoveSelf(deleteMemberData);
    } else if (deleteType === 'Remove Member') {
      onRemoveMember(deleteMemberData);
    } else if (deleteType === 'Remove Scheduled' && deleteScheduleId) {
      onDeleteScheduleConfirmed({
        scheduleId: deleteScheduleId,
        userGuid: deleteMemberData.userId,
        incidentMemberId: deleteMemberData.incidentMemberId ?? '',
      });
    }

    setShowConfirmDialog(false);
    setDeleteMemberData(null);
    setDeleteScheduleId(undefined);
    setDeleteType(null);
  };

  const handleToggleButtonClick = (value: string, row: RosterEntry) => {
    if (value === 'Checked-In') {
      if (row.active && row.activeTime) {
        return;
      }
      onMarkActive(row);
      return;
    }

    if (value === 'Checked Out' || value === 'Checked-Out') {
      if (!(row.active && row.activeTime)) {
        return;
      }
      setDeactivateRow(row);
      setNotActiveMemberDialogOpen(true);
    }
  };

  const deleteConfig = deleteType ? DELETE_TYPE_CONFIG[deleteType] : null;

  return (
    <HwcgRosterActivationProvider>
      <div data-tour="external-collaboration">
        <div data-tour="incident-roster-workspace">
          <RosterActions
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            showList={hwcg}
            toolbarExtra={
              showChartChrome ? (
                <RosterChartChromeButtons
                  sidebarCollapsed={sidebarCollapsed}
                  onSidebarCollapsedChange={setSidebarCollapsed}
                  rosterFilters={chartFilters}
                  onRosterFiltersChange={setChartFilters}
                  positions={positions}
                  hwcgPeriodChoices={periodChoices}
                  iconOnlyFilter
                  onPreviewIcs207={
                    icsFormPdf
                      ? () => {
                          void chartRef.current?.previewIcs207();
                        }
                      : undefined
                  }
                  previewDisabled={viewMode !== 'orgchart'}
                  isExporting={icsFormPdf?.isExporting ?? false}
                />
              ) : undefined
            }
          />

          {hwcg && !isDraft && incidentId && groupId ? (
            <HwcgRosterActivationPanel
              incidentId={incidentId}
              groupId={groupId}
              {...activationPanelPeriod}
            />
          ) : null}

          {viewMode === 'analytics' ? (
            <div className="form-block">
              <div className="tw:flex tw:h-[600px] tw:min-h-0 tw:items-stretch tw:gap-3">
                {!sidebarCollapsed ? (
                  <RosterTemplateSidebar
                    selectedPositionId={null}
                    onCollapse={() => setSidebarCollapsed(true)}
                  />
                ) : null}
                <div className="tw:min-h-0 tw:min-w-0 tw:flex-1 tw:overflow-auto">
                  <RosterAnalyticsView positions={positions} />
                </div>
              </div>
            </div>
          ) : viewMode === 'list' && incidentId && groupId ? (
            <RosterListView
              incidentId={incidentId}
              groupId={groupId}
              positions={positions}
              shown={chartPeriod ? chartPeriod.shown : (rosterData ?? [])}
              later={chartPeriod?.later}
              periodLabels={chartPeriod?.labels}
              draftMembers={draftRosterData}
              plannedPeriodId={panelPlanned?.id ?? null}
              filters={chartFilters}
              onFiltersChange={setChartFilters}
              periodChoices={periodChoices}
              editablePositionIds={editablePositionIds}
            />
          ) : viewMode === 'table' ? (
            <div className="form-block">
              <div
                className="tw:mb-2 tw:flex tw:flex-wrap tw:items-center tw:gap-2"
                data-tour="roster-search-bar"
              >
                <div className="tw:relative tw:min-w-[12rem] tw:flex-1">
                  <Search className="tw:pointer-events-none tw:absolute tw:top-1/2 tw:left-2.5 tw:h-3.5 tw:w-3.5 tw:-translate-y-1/2 tw:text-muted-foreground" />
                  <Input
                    type="search"
                    value={tableFilters.search}
                    onChange={(event) =>
                      setTableFilters((current) => ({
                        ...current,
                        search: event.target.value,
                      }))
                    }
                    placeholder="Search by name, email, or position"
                    aria-label="Search roster members"
                    data-tour="roster-search"
                    className="tw:h-8 tw:pl-8 tw:text-xs"
                  />
                </div>
                <RosterFilterMenu
                  filters={tableFilters}
                  onFiltersChange={setTableFilters}
                  positions={positions}
                  hwcgPeriodChoices={periodChoices}
                />
              </div>
              {tableFilterChips.length > 0 ? (
                <div className="tw:mb-2 tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
                  {tableFilterChips.map((chip) => (
                    <Badge
                      key={chip.id}
                      variant="outline"
                      className="tw:h-6 tw:gap-1 tw:pr-1"
                    >
                      <span className="tw:max-w-[12rem] tw:truncate">
                        {chip.label}
                      </span>
                      <button
                        type="button"
                        className="tw:inline-flex tw:rounded-sm tw:p-0.5 tw:text-muted-foreground tw:hover:bg-muted tw:hover:text-foreground"
                        aria-label={`Remove ${chip.label} filter`}
                        onClick={() =>
                          setTableFilters((current) =>
                            dismissRosterFilterChip(current, chip.id)
                          )
                        }
                      >
                        <X className="tw:size-3" />
                      </button>
                    </Badge>
                  ))}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setTableFilters(EMPTY_ROSTER_FILTERS)}
                  >
                    Clear all
                  </Button>
                </div>
              ) : null}
              <RosterTable
                rosterData={
                  tablePeriod
                    ? [...tablePeriod.shown, ...tablePeriod.later]
                    : rosterData
                }
                hwcgPeriodLabels={tablePeriod?.labels}
                isRosterLoading={isRosterLoading}
                visiblePositionIds={tableVisiblePositionIds}
                memberSearch={tableFilters.search}
                editMemberButtonClicked={editMemberButtonClicked}
                removeMemberButtonClicked={removeMemberButtonClicked}
                removeSelfFromIncidentButtonClicked={
                  removeSelfFromIncidentButtonClicked
                }
                onDeleteSchedule={onDeleteSchedule}
                onToggleButtonClick={handleToggleButtonClick}
                simplified={false}
                currentUserHasApprovalAuthority={
                  currentUserHasApprovalAuthority
                }
                onToggleApprovalAuthority={onToggleApprovalAuthority}
                isTogglingAuthority={isTogglingAuthority}
                showApprovalAuthority={showApprovalAuthority}
              />
            </div>
          ) : (
            <div className="my-4" data-tour="hwcg-roster-shell">
              <OrganizationChart
                ref={chartRef}
                incidentMembers={chartPeriod ? chartPeriod.shown : rosterData}
                draftIncidentMembers={draftRosterData}
                hwcgLaterMembers={chartPeriod ? chartPeriod.later : null}
                hwcgPeriodLabels={chartPeriod?.labels}
                hwcgPersonStatus={hwcgPersonStatus}
                hideToolbar
                sidebarCollapsed={sidebarCollapsed}
                onSidebarCollapsedChange={setSidebarCollapsed}
                rosterFilters={chartFilters}
                onRosterFiltersChange={setChartFilters}
                editablePositionIds={editablePositionIds}
              />
            </div>
          )}

          {isEditMemberDialogOpen && memberSelectedForEditing && (
            <IncidentEditMemberDialog
              show={isEditMemberDialogOpen}
              onClose={() => setIsEditMemberDialogOpen(false)}
              member={memberSelectedForEditing}
            />
          )}

          {notActiveMemberDialogOpen && (
            <IncidentNonActiveConfirmDialog
              show={notActiveMemberDialogOpen}
              onClose={() => setNotActiveMemberDialogOpen(false)}
              active_time={deactivateRow?.activeTime}
              user_guid={deactivateRow?.userId}
              incident_member_id={deactivateRow?.incidentMemberId}
            />
          )}

          {showConfirmDialog && deleteConfig && (
            <StylishConfirmDialog
              show={showConfirmDialog}
              dialogTitle={deleteConfig.title}
              dialogContent={deleteConfig.content}
              dialogButtonText={deleteConfig.buttonText}
              onClose={() => setShowConfirmDialog(false)}
              onConfirm={onDeleteConfirmation}
            />
          )}
        </div>
      </div>
    </HwcgRosterActivationProvider>
  );
};

export default IncidentRosterView;
